import React, { useState, useEffect } from 'react';
import VendorsTabEnhanced from './VendorsTab';
import axios from './lib/axiosClient';
import TaxSettingsTab from './TaxSettingsTab';
import TeamManagement from './TeamManagement';
import ContractManager from './ContractManager';
import CustomerRentalTerms from './CustomerRentalTerms';
import ParallelRunSettings from './ParallelRunSettings';
import { useTenant } from './contexts/TenantContext';

const C = {
  bg:'#1a1c28', card:'#252838', border:'#2e3150', text:'#e2e8f0',
  muted:'#94a3b8', accent:'#f97316', green:'#4ade80', red:'#f87171',
  blue:'#60a5fa', amber:'#fbbf24',
};

// US zones first (the current customers), then the rest the browser knows.
const TIMEZONES = (() => {
  const us = ['America/New_York','America/Chicago','America/Denver','America/Phoenix','America/Los_Angeles','America/Anchorage','Pacific/Honolulu'];
  let all = [];
  try { all = Intl.supportedValuesOf('timeZone'); } catch { all = []; }
  return [...us, ...all.filter(z => !us.includes(z))];
})();


// ── Category Custom Field Manager ─────────────────────────────
function CategoryFieldManager({ axios }) {
  const [cats, setCats] = React.useState([]);
  const [selected, setSelected] = React.useState(null);
  const [newCatName, setNewCatName] = React.useState('');
  const [newFieldLabel, setNewFieldLabel] = React.useState('');
  const [newFieldType, setNewFieldType] = React.useState('text');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    axios.get('/api/rentals/categories')
      .then(r => setCats(r.data.categories || []))
      .catch(() => {});
  }, []);

  const selectedCat = cats.find(c => c.id === selected);

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const r = await axios.post('/api/rentals/categories', { name: newCatName.trim() });
      setCats(p => [...p, r.data.category]);
      setNewCatName('');
    } catch(e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const addField = async () => {
    if (!newFieldLabel.trim() || !selectedCat) return;
    const key = newFieldLabel.trim().toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'');
    const schema = [...(selectedCat.custom_field_schema || []), { key, label: newFieldLabel.trim(), type: newFieldType }];
    setSaving(true);
    try {
      const r = await axios.patch(`/api/rentals/categories/${selectedCat.id}`, { custom_field_schema: schema });
      setCats(p => p.map(c => c.id === selectedCat.id ? r.data.category : c));
      setNewFieldLabel('');
      setNewFieldType('text');
    } catch(e) { alert('Failed to save'); }
    setSaving(false);
  };

  const removeField = async (idx) => {
    if (!selectedCat) return;
    const schema = (selectedCat.custom_field_schema || []).filter((_,i) => i !== idx);
    try {
      const r = await axios.patch(`/api/rentals/categories/${selectedCat.id}`, { custom_field_schema: schema });
      setCats(p => p.map(c => c.id === selectedCat.id ? r.data.category : c));
    } catch(e) { alert('Failed'); }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`/api/rentals/categories/${id}`);
      setCats(p => p.filter(c => c.id !== id));
      if (selected === id) setSelected(null);
    } catch(e) { alert('Failed'); }
  };

  const C = { border:'#2e3150', muted:'#64748b', text:'#e2e8f0', orange:'#f97316' };
  const inp = { background:'#181928', border:`1px solid ${C.border}`, borderRadius:6, padding:'7px 10px', color:C.text, fontSize:12, outline:'none' };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:16, minHeight:300 }}>
      {/* Left: category list */}
      <div>
        <div style={{ fontSize:11, fontWeight:700, color:C.orange, marginBottom:10, textTransform:'uppercase', letterSpacing:.5 }}>Categories</div>
        {cats.map(c => (
          <div key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 10px', borderRadius:6, marginBottom:4, cursor:'pointer', background: selected===c.id ? '#131420' : 'transparent', border:`1px solid ${selected===c.id ? C.orange : C.border}` }}
            onClick={() => setSelected(c.id)}>
            <span style={{ fontSize:12, color:C.text, fontWeight: selected===c.id ? 700 : 400 }}>{c.name}</span>
            <button onClick={e=>{e.stopPropagation();deleteCategory(c.id);}} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:12, padding:'0 2px' }}>✕</button>
          </div>
        ))}
        <div style={{ display:'flex', gap:6, marginTop:10 }}>
          <input style={{ ...inp, flex:1 }} placeholder="New category..." value={newCatName} onChange={e=>setNewCatName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addCategory()} />
          <button onClick={addCategory} style={{ background:C.orange, border:'none', borderRadius:6, color:'#fff', fontSize:12, fontWeight:700, padding:'7px 10px', cursor:'pointer' }}>+</button>
        </div>
      </div>

      {/* Right: field editor */}
      <div>
        {!selectedCat ? (
          <div style={{ color:C.muted, fontSize:13, paddingTop:40, textAlign:'center' }}>Select a category to manage its custom fields</div>
        ) : (
          <>
            <div style={{ fontSize:11, fontWeight:700, color:C.orange, marginBottom:10, textTransform:'uppercase', letterSpacing:.5 }}>{selectedCat.name} — Custom Fields</div>
            {(selectedCat.custom_field_schema || []).length === 0
              ? <div style={{ color:C.muted, fontSize:12, marginBottom:12 }}>No custom fields yet. Add one below.</div>
              : (selectedCat.custom_field_schema || []).map((field, idx) => (
                <div key={idx} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'#1e2235', borderRadius:6, marginBottom:6, border:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:12, color:C.text, fontWeight:600, flex:1 }}>{field.label || field.name}</span>
                  <span style={{ fontSize:10, color:C.muted, background:'#2e3150', padding:'2px 8px', borderRadius:4 }}>{field.type || 'text'}</span>
                  <button onClick={()=>removeField(idx)} style={{ background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:12 }}>✕</button>
                </div>
              ))
            }
            <div style={{ display:'flex', gap:8, marginTop:12, alignItems:'center' }}>
              <input style={{ ...inp, flex:1 }} placeholder="Field name (e.g. Grit, Bond, Size...)" value={newFieldLabel} onChange={e=>setNewFieldLabel(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addField()} />
              <select style={{ ...inp, width:100 }} value={newFieldType} onChange={e=>setNewFieldType(e.target.value)}>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="select">Dropdown</option>
              </select>
              <button onClick={addField} disabled={saving} style={{ background:C.orange, border:'none', borderRadius:6, color:'#fff', fontSize:12, fontWeight:700, padding:'7px 14px', cursor:'pointer' }}>
                {saving ? '...' : '+ Add Field'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function RentalSettings({ initialTab }) {
  const { tenant } = useTenant();
  const isOwner = tenant?.userRole === 'owner';
  const [s, setS]       = useState({
    business_name:'', notification_email:'', business_address:'',
    free_miles:10, rate_per_mile:2.50, min_delivery_fee:0,
    quote_expiry_days:7, quote_notes:'',
    rate_presets: [],
    item_categories: ['Grinding','Scarifying','Blasting','Application','Polishing','Accessories','Dust Control','Cutting','Prep'],
    pickup_reminder_days: 1, return_reminder_days: 1, overdue_alert_repeat_days: 0, portal_slug: '',
    smtp_host:'', smtp_port:587, smtp_user:'', smtp_pass:'', smtp_from_name:'', smtp_from_email:'',
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState('');
  const [maintenance, setMaintenance] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [newMaint, setNewMaint] = useState({ rental_item_id:'', start_date:'', end_date:'', reason:'maintenance', notes:'' });
  const [newRule, setNewRule]   = useState({ name:'', rule_type:'date_range', multiplier:1.0, start_date:'', end_date:'', days_of_week:[] });
  const [items, setItems]       = useState([]);
  const [tab, setTab] = useState(initialTab || 'general');
  const [pwOld,     setPwOld]     = React.useState('');
  const [pwNew,     setPwNew]     = React.useState('');
  const [pwConf,    setPwConf]    = React.useState('');
  const [pwSaving,  setPwSaving]  = React.useState(false);
  const [pwMsg,     setPwMsg]     = React.useState(null);

  // Locations
  const [locations, setLocations]   = useState([]);
  const [locForm, setLocForm]       = useState({ name:'', address:'', city:'', state:'', zip:'', phone:'', is_default:false, governing_state:'', venue:'' });
  const [editingLoc, setEditingLoc] = useState(null);
  const [locSaving, setLocSaving]   = useState(false);
  const [taxParts, setTaxParts]     = useState([]);

  useEffect(() => {
    axios.get('/api/rentals/settings').then(r => {
      if (r.data.settings) {
        const loaded = r.data.settings;
        // If no categories saved yet, keep the defaults
        if (!loaded.item_categories || loaded.item_categories.length === 0) {
          delete loaded.item_categories;
        }
        setS(p => ({ ...p, ...loaded }));
      }
    }).catch(() => {});
    axios.get('/api/rentals/maintenance').then(r => setMaintenance(r.data.maintenance||[])).catch(()=>{});
    axios.get('/api/rentals/pricing-rules').then(r => setPricingRules(r.data.rules||[])).catch(()=>{});
    axios.get('/api/rentals/items').then(r => setItems(r.data.items||[])).catch(()=>{});
    axios.get('/api/rental-locations/locations').then(r => setLocations(r.data.locations||[])).catch(()=>{});
    axios.get('/api/rentals/tax/parts').then(r => setTaxParts(r.data.parts||[])).catch(()=>{});
  }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      // A blank SMTP password means "keep the stored one" — the server never
      // returns it, so the field is empty on load.
      // Only editable fields go to the server; it rejects anything else.
      const EDITABLE = ['free_miles','rate_per_mile','min_delivery_fee','business_address','quote_expiry_days','quote_notes','notification_email','business_name','rate_presets','overdue_interest_rate','overdue_grace_days','overdue_interest_enabled','pickup_reminder_days','return_reminder_days','overdue_alert_repeat_days','portal_slug','smtp_host','smtp_port','smtp_user','smtp_from_name','smtp_from_email','booking_deposit_type','booking_deposit_value','security_deposit_collection','same_day_turnaround',
        'late_return_enabled','late_return_grace_hours','late_return_mode','late_return_value','late_return_cap_type','late_return_cap_value','late_return_notice_days','late_return_message'];
      const body = Object.fromEntries(EDITABLE.filter(k => s[k] !== undefined).map(k => [k, s[k]]));
      if (s.smtp_pass) body.smtp_pass = s.smtp_pass;
      await axios.put('/api/rentals/settings', body);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(err) { setError(err.response?.data?.error || 'Could not save settings'); }
    setSaving(false);
  }

  const addMaintenance = async () => {
    if (!newMaint.rental_item_id || !newMaint.start_date || !newMaint.end_date) return alert('Select item and dates');
    try {
      const r = await axios.post('/api/rentals/maintenance', newMaint);
      setMaintenance(p => [...p, r.data.maintenance]);
      setNewMaint({ rental_item_id:'', start_date:'', end_date:'', reason:'maintenance', notes:'' });
    } catch(e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const deleteMaintenance = async (id) => {
    await axios.delete(`/api/rentals/maintenance/${id}`);
    setMaintenance(p => p.filter(m => m.id !== id));
  };

  const addPricingRule = async () => {
    if (!newRule.name || !newRule.multiplier) return alert('Name and multiplier required');
    try {
      const r = await axios.post('/api/rentals/pricing-rules', newRule);
      setPricingRules(p => [...p, r.data.rule]);
      setNewRule({ name:'', rule_type:'date_range', multiplier:1.0, start_date:'', end_date:'', days_of_week:[] });
    } catch(e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const deletePricingRule = async (id) => {
    await axios.delete(`/api/rentals/pricing-rules/${id}`);
    setPricingRules(p => p.filter(r => r.id !== id));
  };

  const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const inp  = { padding:'9px 12px', borderRadius:8, border:`1px solid ${C.border}`, background:'#181928', color:C.text, fontSize:13, width:'100%', boxSizing:'border-box', outline:'none' };
  const lbl  = { fontSize:11, fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.5px', display:'block', marginBottom:5, marginTop:14 };
  const card = { background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px 24px', marginBottom:20 };
  const sec  = { fontSize:13, fontWeight:700, color:C.accent, marginBottom:12, display:'flex', alignItems:'center', gap:8 };

  const exampleOneWay = 20;
  const rt = exampleOneWay * 2;
  const free = Number(s.free_miles || 10);
  const rate = Number(s.rate_per_mile || 2.50);
  const min  = Number(s.min_delivery_fee || 0);
  const exFee = rt <= free ? min : Math.max(min, (rt - free) * rate);

  const NAV = [
    // Tax and Team are owner-only (enforced server-side too).
    { group:'BUSINESS',    items:[['general','🏢','General'],['locations','📍','Locations'],...(isOwner ? [['tax','🧾','Tax']] : [])] },
    // Pricing Rules and Maintenance windows are hidden until they are wired in — see the note in General.
    { group:'OPERATIONS',  items:[['latefees','⏰','Late Fees'],['vendors','🏭','Vendors']] },
    { group:'DOCUMENTS',   items:[['contracts','📄','Contracts']] },
    ...(isOwner ? [{ group:'TEAM',        items:[['team','👥','Team']] }] : []),
    { group:'ACCOUNT',     items:[['password','🔒','Password']] },
  ];

  return (
    <div style={{ fontFamily:"'DM Sans',Arial,sans-serif", minHeight:'100vh' }}>
      {/* Header */}
      <div style={{ padding:'24px 28px 16px', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ fontSize:22, fontWeight:800, color:C.text, marginBottom:2 }}>⚙️ Settings</div>
        <div style={{ fontSize:13, color:C.muted }}>Manage your rental business configuration</div>
      </div>

      {/* Two-column layout */}
      <div style={{ display:'grid', gridTemplateColumns:'200px 1fr', minHeight:'calc(100vh - 80px)' }}>

        {/* Left nav */}
        <div style={{ borderRight:`1px solid ${C.border}`, padding:'20px 12px', background:'#1a1c28' }}>
          {NAV.map(({ group, items }) => (
            <div key={group} style={{ marginBottom:24 }}>
              <div style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'1px', padding:'0 10px', marginBottom:6 }}>{group}</div>
              {items.map(([v, icon, label]) => (
                <button key={v} onClick={() => setTab(v)}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, border:'none', background: tab===v ? 'rgba(249,115,22,0.12)' : 'transparent', color: tab===v ? C.accent : C.muted, fontWeight: tab===v ? 700 : 400, fontSize:13, cursor:'pointer', textAlign:'left', marginBottom:2, transition:'all 0.15s' }}>
                  <span style={{ fontSize:15 }}>{icon}</span>
                  {label}
                  {tab===v && <span style={{ marginLeft:'auto', width:3, height:16, borderRadius:2, background:C.accent, flexShrink:0 }} />}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Right content */}
        <div style={{ padding:'28px 32px', maxWidth:780, overflowY:'auto' }}>

      {/* ── GENERAL TAB ── */}
      {tab === 'general' && !isOwner && (
        <div style={{ padding:'10px 14px', marginBottom:14, borderRadius:8, background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.35)', color:'#fbbf24', fontSize:13 }}>
          Business settings can be viewed by staff but only the owner can save changes.
        </div>
      )}
      {tab === 'general' && (
        <form onSubmit={save}>
          <div style={card}>
            <div style={sec}>🏢 Business Info</div>
            <label style={lbl}>Business Name</label>
            <input style={inp} value={s.business_name||''} onChange={e=>setS(p=>({...p,business_name:e.target.value}))} placeholder="Your business name" />
            <label style={lbl}>Notification Email</label>
            <input style={inp} type="email" value={s.notification_email||''} onChange={e=>setS(p=>({...p,notification_email:e.target.value}))} placeholder="owner@yourbusiness.com" />
            <div style={{ fontSize:11, color:'#e2e8f0', marginTop:4 }}>Quote requests from your storefront and system alerts will be sent here.</div>
          </div>
          <div style={card}>
            <div style={sec}>🚚 Delivery & Mileage</div>
            <label style={lbl}>Business Address (delivery origin)</label>
            <input style={inp} value={s.business_address||''} onChange={e=>setS(p=>({...p,business_address:e.target.value}))} placeholder="1234 Main St, Las Vegas, NV 89101" />
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14, marginTop:4 }}>
              <div><label style={lbl}>Free Miles (round trip)</label><input style={inp} type="number" min="0" value={s.free_miles} onChange={e=>setS(p=>({...p,free_miles:e.target.value}))} /></div>
              <div><label style={lbl}>Rate per Mile (after free)</label><input style={inp} type="number" min="0" step="0.25" value={s.rate_per_mile} onChange={e=>setS(p=>({...p,rate_per_mile:e.target.value}))} /></div>
              <div><label style={lbl}>Minimum Delivery Fee</label><input style={inp} type="number" min="0" step="1" value={s.min_delivery_fee||0} onChange={e=>setS(p=>({...p,min_delivery_fee:e.target.value}))} /></div>
            </div>
            <div style={{ marginTop:14, padding:'12px 16px', background:'#181928', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, color:'#e2e8f0' }}>
              20-mile one-way: {rt <= free ? <span style={{color:C.green}}>✓ FREE</span> : <span style={{color:C.amber}}>{(rt-free).toFixed(1)} billable mi × ${rate.toFixed(2)} = <strong style={{color:C.accent}}>${exFee.toFixed(2)}</strong></span>}
            </div>
          </div>
          {/* Tax moved to its own tab (Settings → Tax): parts per location + taxability rules. */}
          <div style={card}>
            <div style={sec}>📋 Quote Defaults</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div><label style={lbl}>Quote Expiry (days)</label><input style={inp} type="number" min="1" value={s.quote_expiry_days||7} onChange={e=>setS(p=>({...p,quote_expiry_days:e.target.value}))} /></div>
            </div>
            <label style={lbl}>Default Quote Notes</label>
            <textarea style={{ ...inp, height:80, resize:'vertical' }} value={s.quote_notes||''} onChange={e=>setS(p=>({...p,quote_notes:e.target.value}))} placeholder="Thank you for your interest..." />
          </div>
          <div style={card}>
            <div style={sec}>🏷️ Rate Presets</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>Named pricing presets — 1.0 = full price, 0.9 = 10% off, 1.25 = 25% markup.</div>
            {(s.rate_presets||[]).map((p, idx) => (
              <div key={p.id} style={{ display:'grid', gridTemplateColumns:'1fr 120px 120px 36px', gap:10, marginBottom:10, alignItems:'center' }}>
                <input style={inp} value={p.name} placeholder="Preset name" onChange={e=>setS(prev=>({...prev,rate_presets:prev.rate_presets.map((r,i)=>i===idx?{...r,name:e.target.value}:r)}))} />
                <input style={inp} type="number" min="0.1" step="0.05" value={p.multiplier} onChange={e=>setS(prev=>({...prev,rate_presets:prev.rate_presets.map((r,i)=>i===idx?{...r,multiplier:e.target.value}:r)}))} />
                <div style={{ fontSize:12, color:p.multiplier<1?C.green:p.multiplier>1?C.amber:C.muted }}>
                  {p.multiplier==1?'Full price':p.multiplier<1?`${Math.round((1-p.multiplier)*100)}% off`:`${Math.round((p.multiplier-1)*100)}% up`}
                </div>
                <button type="button" style={{background:'none',border:`1px solid ${C.border}`,borderRadius:6,color:C.red,cursor:'pointer',padding:'6px 10px'}} onClick={()=>setS(prev=>({...prev,rate_presets:prev.rate_presets.filter((_,i)=>i!==idx)}))}>✕</button>
              </div>
            ))}
            <button type="button" style={{padding:'8px 18px',borderRadius:8,border:`1px dashed ${C.border}`,background:'transparent',color:C.muted,cursor:'pointer',fontSize:13,marginTop:4}}
              onClick={()=>setS(prev=>({...prev,rate_presets:[...(prev.rate_presets||[]),{id:Date.now().toString(),name:'',multiplier:1.0}]}))}>+ Add Preset</button>
          </div>
          <div style={card}>
            <div style={sec}>📂 Category Custom Fields</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>
              Categories themselves (names, order, nesting) are managed on the Inventory page — drag to reorder there. Here you attach custom fields to a category.
            </div>
            <div style={{ background:'#252838', border:'1px solid #2e3150', borderRadius:10, padding:16 }}>
              <CategoryFieldManager axios={axios} />
            </div>
          </div>
          <div style={card}>
            <div style={sec}>💳 Deposits & Turnaround</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                <label style={lbl}>Booking deposit</label>
                <select style={inp} value={s.booking_deposit_type||'percent'} onChange={e=>setS(p=>({...p,booking_deposit_type:e.target.value}))}>
                  <option value="percent">Percent of the taxed total</option>
                  <option value="fixed">Fixed amount</option>
                  <option value="full">Full payment up front</option>
                </select>
              </div>
              {s.booking_deposit_type !== 'full' && (
                <div>
                  <label style={lbl}>{s.booking_deposit_type === 'fixed' ? 'Deposit amount ($)' : 'Deposit percent (%)'}</label>
                  <input style={inp} type="number" min="0" step="0.01" value={s.booking_deposit_value ?? 25} onChange={e=>setS(p=>({...p,booking_deposit_value:e.target.value}))} />
                </div>
              )}
              <div>
                <label style={lbl}>Security deposit</label>
                <select style={inp} value={s.security_deposit_collection||'pickup'} onChange={e=>setS(p=>({...p,security_deposit_collection:e.target.value}))}>
                  <option value="pickup">Collected at pickup</option>
                  <option value="charged_at_booking">Charged at booking</option>
                  <option value="card_on_file">Card on file</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Same-day turnaround</label>
                <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:C.text, cursor:'pointer', marginTop:8 }}>
                  <input type="checkbox" checked={!!s.same_day_turnaround} onChange={e=>setS(p=>({...p,same_day_turnaround:e.target.checked}))} style={{ accentColor:C.accent }} />
                  A unit returned today can go out again today
                </label>
              </div>
            </div>
            <div style={{ fontSize:12, color:C.muted, marginTop:8 }}>The booking deposit applies to every new order, quote and storefront checkout; existing orders keep the deposit stamped on them. Per-product turnaround days (Products → edit) override the same-day setting.</div>
          </div>
          <div style={card}>
            <div style={sec}>🔔 Automated Reminder Settings</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={lbl}>Pickup Reminder (days before)</label>
                <input style={inp} type="number" min="0" max="30" value={s.pickup_reminder_days ?? 1} onChange={e=>setS(p=>({...p,pickup_reminder_days:Number(e.target.value)}))} />
                <div style={{fontSize:11,color:C.muted,marginTop:4}}>SMS/email sent X days before pickup. Default: 1 (day before).</div>
              </div>
              <div>
                <label style={lbl}>Return Reminder (days before)</label>
                <input style={inp} type="number" min="0" max="30" value={s.return_reminder_days ?? 1} onChange={e=>setS(p=>({...p,return_reminder_days:Number(e.target.value)}))} />
                <div style={{fontSize:11,color:C.muted,marginTop:4}}>SMS sent X days before return due. Default: 1 (day before).</div>
              </div>
            </div>
            <div style={{ marginTop:12 }}>
              <label style={lbl}>Overdue grace period (days before overdue alerts start)</label>
              <input style={inp} type="number" min="0" value={s.overdue_grace_days||0} onChange={e=>setS(p=>({...p,overdue_grace_days:e.target.value}))} />
              <label style={lbl}>Overdue Alert Repeat (days, 0 = send once)</label>
              <input style={{...inp, maxWidth:160}} type="number" min="0" max="30" value={s.overdue_alert_repeat_days ?? 0} onChange={e=>setS(p=>({...p,overdue_alert_repeat_days:Number(e.target.value)}))} />
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Re-send overdue email every N days until resolved. Set 0 to alert once only.</div>
            </div>
            <div style={{ marginTop:12 }}>
              <label style={lbl}>Portal Slug (for client links in emails)</label>
              <input style={{...inp, maxWidth:240}} value={s.portal_slug||''} onChange={e=>setS(p=>({...p,portal_slug:e.target.value}))} placeholder="e.g. onthegrind" />
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Used to build client-facing portal links in reminder emails.</div>
            </div>
          </div>
          <div style={card}>
            <div style={sec}>📧 Email Settings (SMTP)</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>Send emails from your own email address. Use Gmail app password or your email provider's SMTP credentials. Leave blank to use IVision's default email.</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div><label style={lbl}>From Name</label><input style={inp} value={s.smtp_from_name||''} onChange={e=>setS(p=>({...p,smtp_from_name:e.target.value}))} placeholder="Your business name" /></div>
              <div><label style={lbl}>From Email</label><input style={inp} type="email" value={s.smtp_from_email||''} onChange={e=>setS(p=>({...p,smtp_from_email:e.target.value}))} placeholder="service@onthegrind.pro" /></div>
              <div><label style={lbl}>SMTP Host</label><input style={inp} value={s.smtp_host||''} onChange={e=>setS(p=>({...p,smtp_host:e.target.value}))} placeholder="smtp.gmail.com" /></div>
              <div><label style={lbl}>SMTP Port</label><input style={inp} type="number" value={s.smtp_port||587} onChange={e=>setS(p=>({...p,smtp_port:e.target.value}))} placeholder="587" /></div>
              <div><label style={lbl}>SMTP Username</label><input style={inp} value={s.smtp_user||''} onChange={e=>setS(p=>({...p,smtp_user:e.target.value}))} placeholder="service@onthegrind.pro" /></div>
              <div><label style={lbl}>SMTP Password / App Password</label><input style={inp} type="password" value={s.smtp_pass||''} onChange={e=>setS(p=>({...p,smtp_pass:e.target.value}))} placeholder={s.smtp_pass_set ? 'Password is set — type to replace' : 'Not set'} autoComplete="new-password" /></div>
            </div>
            <div style={{ marginTop:10, padding:'10px 14px', background:'#181928', borderRadius:8, border:`1px solid ${C.border}`, fontSize:12, color:C.muted }}>
              💡 For Gmail: enable 2FA → create an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{color:C.accent}}>App Password</a> → use that as your SMTP password. Host: smtp.gmail.com, Port: 587.
            </div>
          </div>
          <div style={{ ...card, borderStyle:'dashed' }}>
            <div style={sec}>🚧 Coming soon</div>
            <div style={{ fontSize:12, color:C.muted, lineHeight:1.6 }}>Pricing rules and templates (seasonal and weekday multipliers), late-payment interest, and scheduled maintenance windows are not yet applied to prices, balances or availability, so their settings are hidden until they are. Late RETURN fees are live under Late Fees. Nothing already entered has been deleted.</div>
          </div>
          {isOwner && <ParallelRunSettings axios={axios} C={C} card={card} sec={sec} lbl={lbl} inp={inp} />}
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <button type="submit" disabled={saving} style={{padding:'11px 28px',borderRadius:8,border:'none',background:saving?'#7c3a00':C.accent,color:'#fff',fontWeight:700,cursor:'pointer',fontSize:14}}>
              {saved?'✓ Saved!':saving?'Saving...':'Save Settings'}
            </button>
            {saved && <span style={{fontSize:13,color:C.green}}>Changes saved</span>}
            {error && <span style={{fontSize:13,color:C.red}}>⚠️ {error}</span>}
          </div>
        </form>
      )}

      {/* ── MAINTENANCE TAB ── */}
      {tab === 'team' && (
        <TeamManagement />
      )}

      {tab === 'contracts' && (
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>Customer Rental Terms</div>
          <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>What a customer actually signs at checkout/pickup — versioned, and snapshotted on every signed order.</div>
          <CustomerRentalTerms axios={axios} C={C} card={card} sec={sec} lbl={lbl} inp={inp} locations={locations} />

          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginTop:28, marginBottom:4 }}>Business Documents</div>
          <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>Any other paperwork you want to email out — e.g. an equipment-owner/consignment agreement. Not shown to rental customers.</div>
          <ContractManager showFlash={(msg,type)=>{ /* flash handled by parent if passed */ }} />
        </div>
      )}

      {/* ── LOCATIONS TAB ── */}
      {tab === 'locations' && (
        <div>
          <div style={card}>
            <div style={sec}>📍 Locations</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:14 }}>Each location's tax parts are set under Settings → Tax. The default location is used when no location is selected.</div>
            {locations.map(loc => (
              <div key={loc.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:`1px solid ${C.border}` }}>
                {editingLoc?.id === loc.id ? (
                  <div style={{ flex:1 }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1.4fr 1fr 70px', gap:8, marginBottom:8 }}>
                      <input style={inp} value={editingLoc.name} onChange={e=>setEditingLoc(p=>({...p,name:e.target.value}))} placeholder="Location name" />
                      <input style={inp} value={editingLoc.address||''} onChange={e=>setEditingLoc(p=>({...p,address:e.target.value}))} placeholder="Address" />
                      <input style={inp} value={editingLoc.city||''} onChange={e=>setEditingLoc(p=>({...p,city:e.target.value}))} placeholder="City" />
                      <input style={inp} value={editingLoc.state||''} onChange={e=>setEditingLoc(p=>({...p,state:e.target.value}))} placeholder="State" />
                    </div>
                    <div style={{ marginBottom:8 }}>
                      <label style={{...lbl, marginTop:0}}>Time zone</label>
                      <select style={inp} value={editingLoc.timezone||'America/Denver'} onChange={e=>setEditingLoc(p=>({...p,timezone:e.target.value}))}>
                        {TIMEZONES.map(z => <option key={z} value={z}>{z}</option>)}
                      </select>
                      {editingLoc.timezone && editingLoc.timezone !== loc.timezone && (
                        <div data-testid="tz-warning" style={{ fontSize:11, color:'#fbbf24', marginTop:4, lineHeight:1.4 }}>⚠️ Changing the time zone moves this location's "today" for holds, reminders and late fees, and how created times display. Existing orders' pickup and return dates are not changed.</div>
                      )}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1.4fr auto auto', gap:8, alignItems:'end' }}>
                      <div>
                        <label style={{...lbl, marginTop:0}}>Governing State (contracts)</label>
                        <input style={inp} value={editingLoc.governing_state||''} onChange={e=>setEditingLoc(p=>({...p,governing_state:e.target.value}))} placeholder="Defaults to State above" />
                      </div>
                      <div>
                        <label style={{...lbl, marginTop:0}}>Venue (county/city for disputes)</label>
                        <input style={inp} value={editingLoc.venue||''} onChange={e=>setEditingLoc(p=>({...p,venue:e.target.value}))} placeholder="e.g. Washington County, Utah" />
                      </div>
                      <button onClick={async()=>{ if (editingLoc.timezone && editingLoc.timezone !== loc.timezone && !window.confirm(`Change ${loc.name}'s time zone from ${loc.timezone} to ${editingLoc.timezone}? This moves the location's "today" for holds, reminders and late fees. Existing orders' dates are unchanged.`)) return; await axios.put(`/api/rental-locations/locations/${loc.id}`, editingLoc); const r=await axios.get('/api/rental-locations/locations'); setLocations(r.data.locations||[]); setEditingLoc(null); }} style={{ padding:'8px 12px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>Save</button>
                      <button onClick={()=>setEditingLoc(null)} style={{ padding:'8px 10px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:12, cursor:'pointer' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:C.text, display:'flex', alignItems:'center', gap:8 }}>
                        {loc.name}
                        {loc.is_default && <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, background:`${C.accent}22`, color:C.accent, fontWeight:700 }}>DEFAULT</span>}
                      </div>
                      <div style={{ fontSize:11, color:C.muted }}>{[loc.address, loc.city, loc.state].filter(Boolean).join(', ') || 'No address set'}</div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>Governing law: {loc.governing_state || '—'} · Venue: {loc.venue || '—'} · Time zone: {loc.timezone || 'America/Denver'}</div>
                    </div>
                    <button onClick={()=>setTab('tax')} title="Tax parts are managed in Settings → Tax" style={{ fontSize:12, fontWeight:700, color:C.amber, background:'none', border:'none', cursor:'pointer' }}>
                      {(() => { const parts = taxParts.filter(p => p.location_id === loc.id); const dflt = taxParts.filter(p => p.location_id === null);
                        const list = parts.length ? parts : dflt; const pct = Math.round(list.reduce((a,p)=>a+Number(p.rate),0)*10000)/10000;
                        return list.length ? `${pct}% tax${parts.length ? '' : ' (business default)'} →` : 'no tax →'; })()}
                    </button>
                    <button onClick={()=>setEditingLoc({...loc})} style={{ padding:'5px 10px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.blue, fontSize:11, cursor:'pointer' }}>Edit</button>
                    <button onClick={async()=>{ if(!window.confirm('Delete?'))return; await axios.delete(`/api/rental-locations/locations/${loc.id}`); setLocations(p=>p.filter(l=>l.id!==loc.id)); }} style={{ padding:'5px 10px', borderRadius:6, border:`1px solid #3b0a0a`, background:'transparent', color:C.red, fontSize:11, cursor:'pointer' }}>Delete</button>
                  </>
                )}
              </div>
            ))}
            <div style={{ marginTop:16, paddingTop:16, borderTop:`1px solid ${C.border}` }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.muted, marginBottom:10 }}>Add Location</div>
              <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1.4fr 1fr 70px', gap:8, marginBottom:8 }}>
                <input style={inp} placeholder="Name *" value={locForm.name} onChange={e=>setLocForm(p=>({...p,name:e.target.value}))} />
                <input style={inp} placeholder="Address" value={locForm.address} onChange={e=>setLocForm(p=>({...p,address:e.target.value}))} />
                <input style={inp} placeholder="City" value={locForm.city} onChange={e=>setLocForm(p=>({...p,city:e.target.value}))} />
                <input style={inp} placeholder="State" value={locForm.state} onChange={e=>setLocForm(p=>({...p,state:e.target.value}))} />
              </div>
              <div style={{ marginBottom:8 }}>
                <label style={{...lbl, marginTop:0}}>Time zone</label>
                <select style={inp} value={locForm.timezone||'America/Denver'} onChange={e=>setLocForm(p=>({...p,timezone:e.target.value}))}>
                  {TIMEZONES.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1.4fr auto', gap:8, alignItems:'end' }}>
                <div>
                  <label style={{...lbl, marginTop:0}}>Governing State (contracts)</label>
                  <input style={inp} placeholder="Defaults to State above" value={locForm.governing_state||''} onChange={e=>setLocForm(p=>({...p,governing_state:e.target.value}))} />
                </div>
                <div>
                  <label style={{...lbl, marginTop:0}}>Venue (county/city for disputes)</label>
                  <input style={inp} placeholder="e.g. Washington County, Utah" value={locForm.venue||''} onChange={e=>setLocForm(p=>({...p,venue:e.target.value}))} />
                </div>
                <button disabled={locSaving||!locForm.name} onClick={async()=>{ setLocSaving(true); await axios.post('/api/rental-locations/locations', locForm); const r=await axios.get('/api/rental-locations/locations'); setLocations(r.data.locations||[]); setLocForm({name:'',address:'',city:'',state:'',zip:'',phone:'',is_default:false,governing_state:'',venue:''}); setLocSaving(false); }}
                  style={{ padding:'9px 16px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', opacity:locSaving||!locForm.name?0.5:1, whiteSpace:'nowrap' }}>
                  + Add
                </button>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:6, marginTop:8, fontSize:12, color:C.muted, cursor:'pointer' }}>
                <input type="checkbox" checked={locForm.is_default} onChange={e=>setLocForm(p=>({...p,is_default:e.target.checked}))} style={{ accentColor:C.accent }} />
                Set as default location
              </label>
            </div>
          </div>
        </div>
      )}



      {/* ── TAX TAB ── */}
      {tab === 'tax' && (
        <TaxSettingsTab C={C} card={card} sec={sec} lbl={lbl} inp={inp} locations={locations} />
      )}

      {/* ── LATE FEES TAB (late RETURN policy live; late PAYMENT policy coming) ── */}
      {tab === 'latefees' && (
        <form onSubmit={save}>
          {!s.late_return_enabled && (
            <div data-testid="late-return-off" style={{ padding:'12px 16px', marginBottom:14, borderRadius:8, background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.4)', color:'#fca5a5', fontSize:13, lineHeight:1.5 }}>
              <strong>Late return fees are off.</strong> Equipment kept past its return date accrues no fee until you turn this on. Overdue alerts to staff still go out.
            </div>
          )}
          <div style={card}>
            <div style={sec}>⏰ Late return (equipment kept past its return date)</div>
            <label style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, cursor:'pointer' }}>
              <input type="checkbox" checked={!!s.late_return_enabled} onChange={e=>setS(p=>({...p,late_return_enabled:e.target.checked}))} style={{ accentColor:C.accent, width:16, height:16 }} />
              <span style={{ fontWeight:700, fontSize:14, color:C.text }}>Charge a late return fee</span>
            </label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, opacity: s.late_return_enabled ? 1 : 0.55 }}>
              <div>
                <label style={lbl}>Grace period</label>
                <div style={{ display:'flex', gap:8 }}>
                  <input style={inp} type="number" min="0" step="1" value={(s.late_return_grace_hours||0) % 24 === 0 ? (s.late_return_grace_hours||0)/24 : (s.late_return_grace_hours||0)}
                    onChange={e=>{ const n=Number(e.target.value||0); setS(p=>({...p, late_return_grace_hours: (p._graceUnit||'days')==='days' ? n*24 : n })); }} />
                  <select style={{...inp, width:110}} value={s._graceUnit||((s.late_return_grace_hours||0) % 24 === 0 ? 'days' : 'hours')} onChange={e=>setS(p=>({...p,_graceUnit:e.target.value}))}>
                    <option value="days">days</option><option value="hours">hours</option>
                  </select>
                </div>
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>Whole days of grace are subtracted from the late days. The due day itself is never charged.</div>
              </div>
              <div>
                <label style={lbl}>Charge</label>
                <select style={inp} value={s.late_return_mode||'extra_days'} onChange={e=>setS(p=>({...p,late_return_mode:e.target.value}))}>
                  <option value="extra_days">Extra days at the order's day rate</option>
                  <option value="percent_day_rate">A percent of the day rate, per late day</option>
                  <option value="flat_per_day">A flat amount per late day</option>
                </select>
              </div>
              {s.late_return_mode !== 'extra_days' && (
                <div>
                  <label style={lbl}>{s.late_return_mode === 'flat_per_day' ? 'Amount per late day ($)' : 'Percent of the day rate (%)'}</label>
                  <input style={inp} type="number" min="0" step="0.01" value={s.late_return_value ?? 0} onChange={e=>setS(p=>({...p,late_return_value:e.target.value}))} />
                </div>
              )}
              <div>
                <label style={lbl}>Cap</label>
                <div style={{ display:'flex', gap:8 }}>
                  <select style={inp} value={s.late_return_cap_type||'none'} onChange={e=>setS(p=>({...p,late_return_cap_type:e.target.value}))}>
                    <option value="none">No cap</option><option value="amount">Fixed amount ($)</option><option value="percent_of_total">Percent of the order total (%)</option>
                  </select>
                  {(s.late_return_cap_type||'none') !== 'none' && <input style={{...inp, width:120}} type="number" min="0" step="0.01" value={s.late_return_cap_value ?? 0} onChange={e=>setS(p=>({...p,late_return_cap_value:e.target.value}))} />}
                </div>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Message the customer on late day…</label>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {[1,2,3,5,7,10,14].map(d => { const on = (s.late_return_notice_days||[]).map(Number).includes(d); return (
                    <button key={d} type="button" onClick={()=>setS(p=>{ const cur=(p.late_return_notice_days||[]).map(Number); return {...p, late_return_notice_days: on ? cur.filter(x=>x!==d) : [...cur,d].sort((a,b)=>a-b)}; })}
                      style={{ padding:'6px 12px', borderRadius:20, border:`1px solid ${on?C.accent:C.border}`, background: on ? 'rgba(249,115,22,0.15)' : 'transparent', color: on ? C.accent : C.muted, fontSize:12, cursor:'pointer' }}>day {d}</button>); })}
                </div>
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>Sent once per listed day by email and SMS through the outbound gateway (parallel-run rules apply). Days are counted after the grace period.</div>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Message (merge fields: {'{name} {order} {due_date} {days_late} {late_fee} {balance} {business}'})</label>
                <textarea style={{...inp, height:70, resize:'vertical'}} value={s.late_return_message||''} onChange={e=>setS(p=>({...p,late_return_message:e.target.value}))}
                  placeholder="Hi {name}, order {order} was due back on {due_date} and is now {days_late} day(s) late. A late return fee of ${late_fee} has been added; your balance is ${balance}. Please return the equipment or contact {business}." />
              </div>
            </div>
            <div style={{ fontSize:12, color:C.muted, marginTop:10 }}>Fees accrue on the 6-hour schedule for orders that are checked out and past their return date, are re-totalled with tax, and are recorded per order. Changing this policy never rewrites a fee already charged.</div>
          </div>
          <div style={{ ...card, borderStyle:'dashed' }}>
            <div style={sec}>💸 Late payment (balance unpaid past due) — coming soon</div>
            <div style={{ fontSize:12, color:C.muted }}>Grace days, flat fee or interest, frequency, cap and payment reminders. Not yet applied; nothing to configure here yet.</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <button type="submit" disabled={saving} style={{ padding:'11px 28px', borderRadius:8, border:'none', background:saving?'#7c3a00':C.accent, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:14 }}>{saving ? 'Saving...' : 'Save'}</button>
            {saved && <span style={{ color:C.green, fontSize:13 }}>✓ Saved</span>}
            {error && <span style={{ color:C.red, fontSize:13 }}>{error}</span>}
          </div>
        </form>
      )}
      {tab === 'password' && (
        <div style={{ maxWidth:480 }}>
          {pwMsg && (
            <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, background:pwMsg.ok?'rgba(52,211,153,0.1)':'rgba(248,113,113,0.1)', border:`1px solid ${pwMsg.ok?'#34d399':'#f87171'}`, color:pwMsg.ok?'#34d399':'#f87171', fontSize:13 }}>
              {pwMsg.text}
            </div>
          )}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:24 }}>
            {[
              { label:'Current Password', val:pwOld, set:setPwOld },
              { label:'New Password',     val:pwNew, set:setPwNew },
              { label:'Confirm Password', val:pwConf, set:setPwConf },
            ].map(({ label, val, set }) => (
              <div key={label} style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:5 }}>{label}</div>
                <input type="password" value={val} onChange={e=>set(e.target.value)} placeholder="••••••••"
                  style={{ width:'100%', background:'#252838', border:`1px solid ${C.border}`, borderRadius:7, padding:'9px 12px', color:C.text, fontSize:13, boxSizing:'border-box' }} />
              </div>
            ))}
            <button disabled={pwSaving} onClick={async () => {
              if (!pwOld) { setPwMsg({ ok:false, text:'Current password is required.' }); return; }
              if (pwNew.length < 8) { setPwMsg({ ok:false, text:'New password must be at least 8 characters.' }); return; }
              if (pwNew !== pwConf) { setPwMsg({ ok:false, text:'Passwords do not match.' }); return; }
              setPwSaving(true); setPwMsg(null);
              try {
                await axios.post('/api/rentals/change-password', { oldPassword:pwOld, newPassword:pwNew });
                setPwMsg({ ok:true, text:'Password changed successfully.' });
                setPwOld(''); setPwNew(''); setPwConf('');
              } catch(e) {
                setPwMsg({ ok:false, text: e.response?.data?.error || 'Failed to change password.' });
              }
              setPwSaving(false);
            }} style={{ width:'100%', padding:'10px', borderRadius:8, border:'none', background:'#f97316', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', opacity:pwSaving?0.6:1, marginTop:6 }}>
              {pwSaving ? 'Saving...' : 'Update Password'}
            </button>
          </div>
        </div>
      )}

      {tab === 'vendors' && (
        <VendorsTabEnhanced axios={axios} />
      )}
        </div>
      </div>
    </div>
  );
}

function PricingTemplatesSection({ axios, C, card, sec, lbl, inp }) {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', tiers: [{ duration_days: 1, label: 'Daily', multiplier: 1 }] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/rental-price-templates').then(r => setTemplates(r.data.templates || [])).catch(()=>{});
  }, []);

  const resetForm = () => { setForm({ name: '', tiers: [{ duration_days: 1, label: 'Daily', multiplier: 1 }] }); setEditing(null); };

  const addTier = () => setForm(p => ({ ...p, tiers: [...p.tiers, { duration_days: 7, label: '', multiplier: 1 }] }));
  const removeTier = (idx) => setForm(p => ({ ...p, tiers: p.tiers.filter((_, i) => i !== idx) }));
  const updateTier = (idx, key, val) => setForm(p => ({ ...p, tiers: p.tiers.map((t, i) => i === idx ? { ...t, [key]: val } : t) }));

  const saveTemplate = async () => {
    if (!form.name) return alert('Template name is required');
    setSaving(true);
    try {
      if (editing) {
        const r = await axios.put('/api/rental-price-templates/' + editing.id, form);
        setTemplates(p => p.map(t => t.id === editing.id ? r.data.template : t));
      } else {
        const r = await axios.post('/api/rental-price-templates', form);
        setTemplates(p => [...p, r.data.template]);
      }
      resetForm();
    } catch (e) { alert(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  const startEdit = (t) => { setEditing(t); setForm({ name: t.name, tiers: t.tiers || [] }); };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this pricing template?')) return;
    await axios.delete('/api/rental-price-templates/' + id);
    setTemplates(p => p.filter(t => t.id !== id));
  };

  return (
    <div style={card}>
      <div style={sec}>📐 Pricing Templates</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
        Create reusable pricing tier templates. Each tier defines a duration label and a multiplier relative to the base daily rate (e.g. Weekly = x4.25 means base_rate × 4.25).
      </div>

      {/* Form */}
      <div style={{ background: '#181928', border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12 }}>{editing ? 'Edit Template' : 'New Template'}</div>
        <label style={lbl}>Template Name</label>
        <input style={{ ...inp, marginBottom: 12 }} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Standard Equipment, Premium Tools" />

        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.5px' }}>Tiers</div>
        {form.tiers.map((tier, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 100px 36px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <input style={inp} type="number" min="1" value={tier.duration_days} onChange={e => updateTier(idx, 'duration_days', Number(e.target.value))} placeholder="Days" title="Duration days" />
            <input style={inp} value={tier.label} onChange={e => updateTier(idx, 'label', e.target.value)} placeholder="Label (e.g. Weekly)" />
            <input style={inp} type="number" min="0.01" step="0.05" value={tier.multiplier} onChange={e => updateTier(idx, 'multiplier', Number(e.target.value))} placeholder="x1.0" title="Multiplier" />
            {form.tiers.length > 1 && (
              <button type="button" onClick={() => removeTier(idx)} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.red, cursor: 'pointer', padding: '6px 10px' }}>✕</button>
            )}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="button" onClick={addTier} style={{ padding: '6px 14px', borderRadius: 6, border: `1px dashed ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 12 }}>+ Add Tier</button>
          <button type="button" onClick={saveTemplate} disabled={saving} style={{ padding: '6px 18px', borderRadius: 6, border: 'none', background: C.accent, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>{saving ? 'Saving...' : editing ? 'Update' : 'Create Template'}</button>
          {editing && <button type="button" onClick={resetForm} style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 12 }}>Cancel</button>}
        </div>
      </div>

      {/* List */}
      {templates.length === 0 && <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No pricing templates yet</div>}
      {templates.map(t => (
        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: C.text, fontSize: 13 }}>{t.name}</div>
            <div style={{ fontSize: 11, color: C.muted, display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              {(t.tiers || []).map((tier, i) => (
                <span key={i} style={{ padding: '2px 6px', borderRadius: 4, background: `${C.border}`, fontSize: 10 }}>
                  {tier.label || `${tier.duration_days}d`}: x{tier.multiplier}
                </span>
              ))}
            </div>
          </div>
          <button onClick={() => startEdit(t)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.blue, fontSize: 11, cursor: 'pointer' }}>Edit</button>
          <button onClick={() => deleteTemplate(t.id)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #3b0a0a', background: 'transparent', color: C.red, fontSize: 11, cursor: 'pointer' }}>Delete</button>
        </div>
      ))}
    </div>
  );
}

function VendorsTab({ axios, C }) {
  const [vendors, setVendors] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const blank = { name:'', contact:'', phone:'', email:'', website:'', notes:'' };
  const [form, setForm] = useState(blank);

  useEffect(() => {
    axios.get('/api/rental-assets/vendors').then(r => setVendors(r.data.vendors || []));
  }, []);

  const save = async () => {
    setSaving(true);
    if (editing) {
      const r = await axios.patch('/api/rental-assets/vendors/' + editing.id, form);
      setVendors(p => p.map(v => v.id === editing.id ? r.data.vendor : v));
      setEditing(null);
    } else {
      const r = await axios.post('/api/rental-assets/vendors', form);
      setVendors(p => [...p, r.data.vendor]);
      setAdding(false);
    }
    setForm(blank);
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    await axios.delete('/api/rental-assets/vendors/' + id);
    setVendors(p => p.filter(v => v.id !== id));
  };

  const startEdit = (v) => { setEditing(v); setForm({ name:v.name||'', contact:v.contact||'', phone:v.phone||'', email:v.email||'', website:v.website||'', notes:v.notes||'' }); setAdding(false); };

  const inp = { padding:'8px 10px', borderRadius:6, border:`1px solid ${C.border}`, background:C.card, color:C.text, fontSize:13, width:'100%', boxSizing:'border-box' };
  const sec = { fontSize:13, fontWeight:700, color:C.text, marginBottom:16 };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={sec}>🏭 Vendors</div>
        <button onClick={()=>{ setAdding(true); setEditing(null); setForm(blank); }} style={{ padding:'7px 16px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>+ Add Vendor</button>
      </div>

      {(adding || editing) && (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:20, marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:14 }}>{editing ? 'Edit Vendor' : 'New Vendor'}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {[['name','Vendor Name *'],['contact','Contact Person'],['phone','Phone'],['email','Email'],['website','Website']].map(([k,l]) => (
              <div key={k}>
                <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase' }}>{l}</div>
                <input value={form[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} style={inp} />
              </div>
            ))}
            <div style={{ gridColumn:'1/-1' }}>
              <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase' }}>Notes</div>
              <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={{ ...inp, height:60, resize:'vertical' }} />
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button onClick={save} disabled={saving||!form.name} style={{ padding:'7px 18px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>{saving?'Saving...':'Save'}</button>
            <button onClick={()=>{ setAdding(false); setEditing(null); setForm(blank); }} style={{ padding:'7px 14px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:12, cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {vendors.length === 0 && !adding && (
        <div style={{ textAlign:'center', padding:40, color:C.muted, fontSize:13 }}>No vendors yet. Add your equipment suppliers.</div>
      )}

      {vendors.map(v => (
        <div key={v.id} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 18px', marginBottom:12, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:4 }}>{v.name}</div>
            {v.contact && <div style={{ fontSize:12, color:C.muted }}>{v.contact}</div>}
            <div style={{ display:'flex', gap:16, marginTop:4 }}>
              {v.phone && <span style={{ fontSize:12, color:C.muted }}>📞 {v.phone}</span>}
              {v.email && <span style={{ fontSize:12, color:C.muted }}>✉️ {v.email}</span>}
              {v.website && <a href={v.website} target="_blank" rel="noreferrer" style={{ fontSize:12, color:C.accent }}>🌐 {v.website}</a>}
            </div>
            {v.notes && <div style={{ fontSize:11, color:C.muted, marginTop:6, fontStyle:'italic' }}>{v.notes}</div>}
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <button onClick={()=>startEdit(v)} style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:12, cursor:'pointer' }}>Edit</button>
            <button onClick={()=>del(v.id)} style={{ padding:'5px 10px', borderRadius:6, border:'1px solid #3b0a0a', background:'transparent', color:'#f87171', fontSize:12, cursor:'pointer' }}>✕</button>
          </div>
        </div>
      ))}
    </div>
  );
}

